import type { CSSProperties } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Close from '~icons/icons-pix/close'
import {
  UIDialog,
  UIDialogContent,
  UIDialogHeader,
} from 'src/components/ui/Modal'
import { SimplePagination } from 'src/components/ui/SimplePagination'
import { useMyAvailableBoosts } from 'src/services/orpc/extraPackageBoost'
import { translateErrorCode } from 'src/utils/errorCodeTranslator'
import {
  useCloseCouponListDialog,
  useCouponDialogVisible,
  useEffectiveAppliedCoupon,
  useInitializeDefaultCoupon,
  useSetAppliedCoupon,
} from './states'
import { Ticket } from './Ticket'
import type { CouponStatus } from './Ticket/schema'

const PAGE_SIZE = 8

export const CouponListDialog = () => {
  const couponDialogVisible = useCouponDialogVisible()
  const closeCouponListDialog = useCloseCouponListDialog()
  const { t } = useTranslation(['membership'])

  return (
    <UIDialog
      open={couponDialogVisible}
      onClose={closeCouponListDialog}
      dismissable
      className="relative bg-white p-3 rounded-2xl"
    >
      <Close
        className="absolute -right-5 -top-5 w-10 h-10 p-1 cursor-pointer svg-stroke-inherit rounded-full border-2 border-neutral-300 bg-white hover:bg-neutral-100"
        style={{ '--svg-stroke-color': '#525252' } as CSSProperties}
        onClick={closeCouponListDialog}
      />
      <UIDialogHeader
        heading={
          <div className="text-2xl font-bold text-neutral-800">
            {t('membership:extra-pack.coupon-list.title', 'My Coupons')}
          </div>
        }
        className="bg-white rounded-tl-2xl rounded-tr-2xl"
      >
        {({ heading }) => heading}
      </UIDialogHeader>
      <UIDialogContent className="w-[300px] md:w-[700px] bg-white">
        <CouponList />
      </UIDialogContent>
    </UIDialog>
  )
}

export const CouponList = () => {
  const { data: coupons, isPending, isError, error } = useMyAvailableBoosts()
  const appliedCoupon = useEffectiveAppliedCoupon()
  const setAppliedCoupon = useSetAppliedCoupon()
  const { t } = useTranslation(['membership', 'common'])
  const [page, setPage] = useState(0)

  // Auto-select first coupon on initial load
  useInitializeDefaultCoupon()

  if (isPending) {
    return (
      <div className="flex items-center justify-center h-[520px] md:h-[540px]">
        <div className="text-neutral-500">
          {t('common:hint.loading', 'Loading...')}
        </div>
      </div>
    )
  }

  if (isError) {
    const errorMessage = translateErrorCode((error as Error).name)
    return (
      <div className="flex items-center justify-center h-[520px] md:h-[540px]">
        <div className="text-red-500">{errorMessage}</div>
      </div>
    )
  }

  if (!coupons || coupons.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] md:h-[500px] text-neutral-400">
        <div className="text-lg">
          {t('membership:extra-pack.coupon-list.empty', 'No Coupons')}
        </div>
      </div>
    )
  }

  const handleTicketClick = (code: string) => {
    if (appliedCoupon === code) {
      setAppliedCoupon('')
    } else {
      setAppliedCoupon(code)
    }
  }

  const totalPages = Math.ceil(coupons.length / PAGE_SIZE)
  const pagedCoupons = coupons.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  return (
    <>
      <div className="min-h-[400px] md:min-h-[420px]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 overflow-y-auto mb-4 max-h-[400px] md:max-h-none">
          {pagedCoupons.map(coupon => (
            <div
              key={coupon.code}
              className="cursor-pointer h-fit"
              onClick={() => handleTicketClick(coupon.code)}
            >
              <Ticket
                template={coupon.template}
                code={coupon.code}
                boostRatePercentage={coupon.boostRatePercentage}
                status={coupon.status as CouponStatus}
                availableSince={coupon.availableSince}
                availableUntil={coupon.availableUntil}
                isApplied={appliedCoupon === coupon.code}
              />
            </div>
          ))}
        </div>
      </div>
      {totalPages > 1 && (
        <SimplePagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </>
  )
}
